import React from 'react';
import { useParams } from 'react-router-dom';
import BoardHeaderActions from '../components/BoardHeaderActions';
import { useBoardFeature } from '../sharedBoard';
import { Alert, Box, Skeleton, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { useWideBoard } from '../../DashboardRoute';
import { fetchFilterOptions, fetchReportDashboard } from '../../../services/manualDashboardService';
import type { FilterDef } from '../../../types/manualDashboard';
import type { ReportPayload, ReportRow, ReportTerm } from '../../../types/reportDashboard';
import {
  DATE_PRESETS, MetricCard, PageHeader, SelectMenu, SidePanelDock, resolvePreset, type DateRange, type FreshnessStatus,
} from '../../../ui-kit';
import { createSurfaceChatStore } from '../../surface-chat/createSurfaceChatStore';
import { useSurfaceChat } from '../../surface-chat/useSurfaceChat';
import BoardChatPanel from '../components/BoardChatPanel';
import DashboardToolbar from '../components/DashboardToolbar';
import CaseListPanel from './components/CaseListPanel';
import CloseOutPanel from './components/CloseOutPanel';
import MonthTable from './components/MonthTable';
import PersistencyPanel from './components/PersistencyPanel';
import ReportChartPanel from './components/ReportChartPanel';
import { formatReportValue } from './reportFormat';
import { termHint } from './termHint';
import { buildReportSurfaceContext, reportChat } from './reportChat';
import { useReportDownload } from './useReportDownload';

// One store per board, kept outside the component so the conversation
// survives a re-render and two boards never share a thread.
const chatStores = new Map<string, ReturnType<typeof createSurfaceChatStore>>();
function chatStoreFor(dashboardId: string, name: string) {
  let store = chatStores.get(dashboardId);
  if (!store) {
    store = createSurfaceChatStore(name);
    chatStores.set(dashboardId, store);
  }
  return store;
}

const formatInt = (v: number) => formatReportValue(v, 'int');
const formatMoney = (v: number) => formatReportValue(v, 'money');
const volumesSeries = (src: ReportPayload) => src.volumes.series;
const volumesPremium = (src: ReportPayload) => src.volumes.premium_series;
const waitingSplit = (src: ReportPayload, key: string) =>
  src.waiting.splits.find((s) => s.key === key) ?? src.waiting.splits[0];
const cycleSeries = (src: ReportPayload) => src.cycle_time.series;
// Onto the books against off them: the same count, the same cohorts, so one chart.
const booksSeries = (src: ReportPayload) => [
  { key: 'paid', label: 'Policies paid', values: src.paid.monthly },
  { key: 'lapsed', label: 'Policies lapsed', values: src.inactive.monthly },
];
const booksPremium = (src: ReportPayload) => [
  { key: 'paid', label: 'Premium paid', values: src.paid.monthly_premium },
  { key: 'lapsed', label: 'Premium lapsed', values: src.inactive.monthly_premium },
];
const formatDays = (v: number) => formatReportValue(v, 'days');

const PRESETS = [
  DATE_PRESETS.yearToDate,
  DATE_PRESETS.last90Days,
  DATE_PRESETS.thisQuarter,
  DATE_PRESETS.lastQuarter,
  DATE_PRESETS.last12MonthsToDate,
];

/**
 * A chart panel's info icon: yellow, saying how the figure is worked out, when
 * it is a stand-in. The subtitle already says what it counts, so a figure
 * measured as asked needs no icon.
 */
function panelHint(row?: ReportRow) {
  const note = row && (row.missing ?? row.proxy);
  return note ? { info: note, infoTone: 'warning' as const } : {};
}

/** Both of a tile's figures behind its one icon, the premium under its own heading. */
function tileHint(count: ReportRow, premium?: ReportRow) {
  if (!premium) return termHint(count).title;
  return (
    <>
      {termHint(count).title}
      <Box component="span" sx={{ display: 'block', mt: 1.5, fontWeight: 600 }}>Premium</Box>
      {termHint(premium).title}
    </>
  );
}

/**
 * A chart's info icon in premium. The subtitle speaks of counts, so the icon
 * says which premium each line adds up, then what stands in.
 */
function premiumHint(terms: (ReportTerm | undefined)[]) {
  const known = terms.filter((t): t is ReportTerm => Boolean(t));
  // Every caveat, not only the first: a chart can rest on more than one, and a
  // dropped one is a reader taking a figure for more than it is.
  const unique = (lines: (string | null)[]) => lines.filter((l, i) => l && lines.indexOf(l) === i) as string[];
  const notes = unique(known.map((t) => t.proxy ?? t.missing));
  const text = [...unique(known.map((t) => t.definition)), ...notes].join(' ');
  return text ? { info: text, infoTone: notes.length ? ('warning' as const) : undefined } : {};
}

/**
 * The Ops Management Report, from the operations sheet's "Crud Ops Mngt Report" sheet.
 *
 * Laid out in the order she wrote it: her dashboard (the headline counts, her
 * three charts, the month-by-month counts), then placement (the pending
 * report and the aged case lists), then persistency (the paid and inactive
 * reports). Every name is hers, and every figure the customer cannot record as she
 * asked has a yellow info icon saying how it is worked out instead.
 */
export default function ReportDashboardPage() {
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const feature = useBoardFeature();
  const [payload, setPayload] = React.useState<ReportPayload | null>(null);
  const [range, setRange] = React.useState<DateRange>(() => resolvePreset(DATE_PRESETS.yearToDate, dayjs(), 1));
  const [filters, setFilters] = React.useState<Record<string, string[]>>({});
  const [options, setOptions] = React.useState<FilterDef[]>([]);
  const [optionsLoading, setOptionsLoading] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshNonce, setRefreshNonce] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const [trendKey, setTrendKey] = React.useState<string | null>(null);
  // How the open cases are split. What a case waits for is the view that says
  // what would move the backlog, so the chart opens on it.
  const [splitKey, setSplitKey] = React.useState('requirement');
  const [chatOpen, setChatOpen] = React.useState(false);
  useWideBoard(true, chatOpen);

  const chatStore = chatStoreFor(dashboardId ?? 'unknown', 'Ops Management Report');
  const surfaceContextRef = React.useRef<Record<string, unknown> | null>(null);
  const getSurfaceContext = React.useCallback(() => surfaceContextRef.current, []);
  // The conversation is found from the database, not remembered in this
  // browser: the same board opens the same chat on any machine.
  const { sendMessage, sendSQLApproval, stopStream } = useSurfaceChat({
    useStore: chatStore,
    dashboardId: dashboardId ?? '',
    getSurfaceContext,
    enabled: chatOpen,
  });

  const filtersKey = JSON.stringify(filters);
  const download = useReportDownload(dashboardId, filters, range);
  const query = React.useMemo(() => ({ dashboardId, range, filters, refreshNonce }), [dashboardId, range, filters, refreshNonce]);
  // Each chart reads its lines from the report at its own cohort grouping.
  const trendPick = trendKey;
  const waitingSeries = React.useCallback(
    (src: ReportPayload) => waitingSplit(src, splitKey).series, [splitKey]);
  const waitingPremium = React.useCallback(
    (src: ReportPayload) => waitingSplit(src, splitKey).premium_series, [splitKey]);
  const trendSeries = React.useCallback((src: ReportPayload) => {
    const m = src.trend.metrics.find((x) => x.key === (trendPick ?? src.trend.default)) ?? src.trend.metrics[0];
    return m ? [{ key: m.key, label: m.label, values: m.values }] : [];
  }, [trendPick]);
  // The premium on the same cases, where the picked figure counts cases.
  const trendPremium = React.useCallback((src: ReportPayload) => {
    const key = trendPick ?? src.trend.default;
    const p = src.trend.premium[key];
    return p ? [{ key: p.key, label: p.label, values: p.values }] : [];
  }, [trendPick]);

  React.useEffect(() => {
    if (!dashboardId || !feature.enabled) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchReportDashboard(
      dashboardId,
      { filters: JSON.parse(filtersKey), start: range.start, end: range.end, refresh: refreshNonce > 0 },
      controller.signal,
    )
      .then(setPayload)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      });
    return () => controller.abort();
  }, [dashboardId, feature.enabled, filtersKey, range, refreshNonce]);

  React.useEffect(() => {
    if (!dashboardId || !feature.enabled) return undefined;
    const controller = new AbortController();
    setOptionsLoading(true);
    fetchFilterOptions(dashboardId, { start: range.start, end: range.end }, controller.signal)
      .then((d) => setOptions(d.filters))
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setOptionsLoading(false);
      });
    return () => controller.abort();
  }, [dashboardId, feature.enabled, range, refreshNonce]);

  if (!feature.enabled) {
    return <Alert severity="info">Dashboards are not turned on for this workspace.</Alert>;
  }
  if (!payload) {
    return error ? <Alert severity="warning">{error}</Alert> : (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={72} />
        <Skeleton variant="rounded" height={132} />
        <Skeleton variant="rounded" height={320} />
      </Stack>
    );
  }

  const freshness: FreshnessStatus = payload.meta.refreshing
    ? 'updating'
    : payload.meta.status === 'failed' ? 'failed' : payload.meta.stale ? 'stale' : 'fresh';
  const periods = payload.periods;

  const trendMetric = payload.trend?.metrics.find((m) => m.key === (trendKey ?? payload.trend.default)) ?? payload.trend?.metrics[0];

  // The headline figures come as a count then its premium ("Policy Paid",
  // "Policy Paid premium"). Each pair is one tile; a figure with no premium,
  // like the placement rate, is a tile on its own.
  const byKey = new Map(payload.kpis.map((k) => [k.key, k]));
  const tiles = payload.kpis
    .filter((k) => !k.key.endsWith('_premium'))
    .map((count) => ({ count, premium: byKey.get(`${count.key}_premium`) }));


  surfaceContextRef.current = buildReportSurfaceContext(dashboardId ?? '', payload, filters);
  // What the answer is scoped to: the months on screen and each filter picked.
  const chatScope = [
    { key: 'range', label: `${payload.range.start} to ${payload.range.end}` },
    ...options
      .filter((f) => (filters[f.key] ?? []).length > 0)
      .map((f) => ({
        key: f.key,
        label: `${f.label}: ${filters[f.key][0]}${filters[f.key].length > 1 ? ` +${filters[f.key].length - 1}` : ''}`,
      })),
  ];

  const board = (
    <Stack spacing={2}>
      <PageHeader
        ariaLabel="About this report"
        title={payload.spec.name}
        description={payload.spec.description}
        actions={
          <BoardHeaderActions
            dashboardId={dashboardId ?? ''}
            name={payload.spec.name}
            updatedAt={payload.meta.built_at}
            status={freshness}
            refreshing={refreshing}
            onRefresh={() => { setRefreshing(true); setRefreshNonce((n) => n + 1); }}
            loading={loading}
            chatOpen={chatOpen}
            onToggleChat={() => setChatOpen((v) => !v)}
            chatTooltip="Ask about this report"
          />
        }
      />

      <DashboardToolbar
        dateRange={range}
        onDateRangeChange={setRange}
        datePresets={PRESETS}
        minDate={payload.range.history_start}
        maxDate={dayjs().format('YYYY-MM-DD')}
        dateDisplay="month"
        dateLabel="Applications Submitted"
        filters={options}
        applied={filters}
        onApply={setFilters}
        loading={loading}
        optionsLoading={optionsLoading}
        chatOpen={chatOpen}
      />

      {error && <Alert severity="warning">{error}</Alert>}
      {payload.meta.status === 'failed' && payload.meta.error && (
        <Alert severity="warning">
          Could not refresh from the warehouse, so these are the numbers from the last good refresh.
        </Alert>
      )}

      {payload.empty ? (
        <Alert severity="info">The report data has not been loaded yet. Press refresh to load it.</Alert>
      ) : (
        <>
          {/* Two rows of four: the funnel across the top, what is stuck or
              lost underneath. Each tile carries a count and its premium, so
              they go two to a row on a narrow screen. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
            {tiles.map(({ count: k, premium }) => (
              <MetricCard
                key={k.key}
                label={k.label}
                value={k.format === 'days'
                  ? `${formatReportValue(k.total, k.format)} days`
                  : formatReportValue(k.total, k.format)}
                // One icon for the tile: what the count counts, then, under
                // its own heading, what the premium adds up.
                unit={premium ? 'cases' : undefined}
                info={tileHint(k, premium)}
                infoTone={termHint(k).tone === 'warning' || (premium && termHint(premium).tone === 'warning') ? 'warning' : 'neutral'}
                // The premium on the same cases shares the tile: the same
                // subject in another unit, read together.
                // No "cases" and "premium" captions: a plain number beside a
                // dollar figure says which is which, and the tile needs the width.
                secondary={premium ? {
                  value: formatReportValue(premium.total, premium.format),
                  label: 'premium',
                  trend: {
                    points: premium.values.map((v, i) => ({ key: periods[i].key, label: periods[i].label, value: v })),
                    formatValue: (v) => formatReportValue(v, premium.format),
                    label: `${premium.label} by month`,
                  },
                } : undefined}
                loading={loading}
                trend={{
                  points: k.values.map((v, i) => ({ key: periods[i].key, label: periods[i].label, value: v })),
                  formatValue: (v) => formatReportValue(v, k.format),
                  label: `${k.label} by month`,
                }}
              />
            ))}
          </Box>

          {/* The story: what we sent and what stuck, then what is stuck and
              who has to act, then the detail, then the book over time. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
            <ReportChartPanel
              title={payload.volumes.title}
              subtitle={payload.volumes.subtitle}
              payload={payload}
              query={query}
              series={volumesSeries}
              formatValue={formatInt}
              axisLabel="Count of Cases"
              alongside={{ series: volumesPremium, formatValue: formatMoney }}
              swappable
              loading={loading}
            />
            <ReportChartPanel
              title={payload.cycle_time.title}
              subtitle={payload.cycle_time.subtitle}
              {...panelHint(payload.cycle_time.series[0])}
              payload={payload}
              query={query}
              series={cycleSeries}
              formatValue={formatDays}
              axisLabel="Turn Around Time (Median)"
              loading={loading}
            />
          </Box>

          <ReportChartPanel
            kind="stacked"
            title={payload.waiting.title}
            subtitle={payload.waiting.subtitle}
            {...premiumHint([payload.waiting.note, payload.waiting.requirement_note])}
            actions={
              <SelectMenu
                label="View by"
                value={splitKey}
                options={payload.waiting.splits.map((sp) => ({ value: sp.key, label: sp.label }))}
                onChange={setSplitKey}
              />
            }
            payload={payload}
            query={query}
            series={waitingSeries}
            formatValue={formatInt}
            axisLabel="Count of Cases"
            alongside={{ series: waitingPremium, formatValue: formatMoney }}
            swappable
            loading={loading}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
            {trendMetric && (
              <ReportChartPanel
                title={payload.trend.title}
                subtitle={trendMetric.definition}
                {...panelHint(trendMetric)}
                actions={
                  <SelectMenu
                    label="Metric"
                    value={trendMetric.key}
                    options={payload.trend.metrics.map((m) => ({ value: m.key, label: m.label }))}
                    onChange={setTrendKey}
                  />
                }
                payload={payload}
                query={query}
                series={trendSeries}
                formatValue={(v) => formatReportValue(v, trendMetric.format)}
                alongside={payload.trend.premium[trendMetric.key]
                  ? { series: trendPremium, formatValue: formatMoney }
                  : undefined}
                swappable
                loading={loading}
              />
            )}
          </Box>

          {/* What someone has to act on today, before the rest of the detail. */}
          <CaseListPanel
            title="At Risk of Closing Out in 2 Weeks"
            subtitle="Cases still waiting on the carrier that are within two weeks of the day we stop counting on a decision. Past that day a case almost never succeeds. Fewest days left first."
            payload={payload}
            query={query}
            lists={[{ block: 'closing_out', listTerm: 'closing_out', daysTerm: 'days_left', receivedTerm: 'received_at_carrier', carrierFirst: true }]}
            download={download}
            loading={loading}
          />

          <CaseListPanel
            title="Issued Cases With No Premium Paid Yet"
            subtitle="ISSUED REPORT. Policies the carrier has issued that are waiting on their first payment, longest waiting first."
            payload={payload}
            query={query}
            lists={[{ block: 'issued', listTerm: 'policies_issued', daysTerm: 'days_since_issued', showDecision: true, showDelivered: true }]}
            download={download}
            loading={loading}
          />

          <CaseListPanel
            title="Cases By Status"
            subtitle="Aged reporting. Cases one by one, longest waiting first. Choose a status to see its cases."
            payload={payload}
            query={query}
            lists={[
              { block: 'pending_uw', listTerm: 'pending_in_uw', daysTerm: 'days_aged_uw', receivedTerm: 'received_in_uw' },
              { block: 'pending_carrier', listTerm: 'pending_carrier', daysTerm: 'days_aged_carrier', receivedTerm: 'received_at_carrier' },
              { block: 'approved_as_is', listTerm: 'approved_as_is', daysTerm: 'days_since_decision', showDecision: true },
              { block: 'approved_better', listTerm: 'approved_better', daysTerm: 'days_since_decision', showDecision: true },
              { block: 'approved_worse', listTerm: 'approved_worse', daysTerm: 'days_since_decision', showDecision: true },
              { block: 'declined', listTerm: 'declined', daysTerm: 'days_since_decision', showReason: true },
              { block: 'closed_lost', listTerm: 'closed_lost', daysTerm: 'days_aged_carrier', receivedTerm: 'received_at_carrier', carrierFirst: true },
            ]}
            download={download}
            loading={loading}
          />

          <MonthTable block="dashboard_table" payload={payload} query={query} download={download} loading={loading} />

          {/* The book after placement: what went on it, and how long it stays. */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
            <ReportChartPanel
              title="Policies paid and lapsed"
              subtitle="PAID REPORT and INACTIVE REPORT. Each point counts the policies that started being paid for in that month, and the policies that lapsed in that month."
              payload={payload}
              query={query}
              series={booksSeries}
              formatValue={formatInt}
              axisLabel="Count of Policies"
              alongside={{ series: booksPremium, formatValue: formatMoney }}
              swappable
              loading={loading}
            />
            <PersistencyPanel payload={payload} query={query} download={download} loading={loading} />
          </Box>

          <CloseOutPanel payload={payload} loading={loading} />
        </>
      )}
    </Stack>
  );

  return (
    <SidePanelDock
      open={chatOpen}
      storageKey="klaris_board_chat_width"
      label="Resize chat panel"
      panel={
        <BoardChatPanel
          useStore={chatStore}
          boardName={payload.spec.name}
          place={reportChat}
          onSend={sendMessage}
          onStop={stopStream}
          onSQLApproval={sendSQLApproval}
          onClose={() => setChatOpen(false)}
          contextChips={chatScope}
        />
      }
    >
      {board}
    </SidePanelDock>
  );
}
